import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DollarSign, Save } from 'lucide-react';
import { toast } from 'sonner';
import { fmtNum } from '@/utils/currency';

export default function CurrencySettings() {
  const { token } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    base_currency: 'USD',
    exchange_rates: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/currency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/currency`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Currency settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRate = (currency, rate) => {
    setSettings({
      ...settings,
      exchange_rates: settings.exchange_rates.map(r =>
        r.currency === currency ? { ...r, rate_to_usd: parseFloat(rate) } : r
      )
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Currency & Exchange Rates</h1>
          <p className="text-slate-600 mt-1">Configure exchange rates for currency conversion</p>
        </div>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <DollarSign className="w-6 h-6 text-emerald-600" />
            <h2 className="text-xl font-bold text-slate-900">Exchange Rates</h2>
          </div>

          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Base Currency:</strong> USD ($)
              </p>
              <p className="text-xs text-blue-700 mt-1">
                All amounts will be converted to USD for accounting purposes.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Exchange Rates to USD</h3>
              
              {settings.exchange_rates.map((rate) => (
                <div key={rate.currency} className="flex items-center gap-4">
                  <div className="w-20 font-semibold text-slate-700">
                    1 USD =
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={rate.rate_to_usd}
                    onChange={(e) => updateRate(rate.currency, e.target.value)}
                    className="w-32"
                  />
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-700">{rate.currency}</span>
                    <span className="text-xs text-slate-500">
                      ({rate.currency === 'MVR' ? 'Maldivian Rufiyaa' : 'Chinese Yuan'})
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-slate-900 mb-2">Example Conversions:</h4>
              <div className="space-y-1 text-sm text-slate-600">
                {settings.exchange_rates.map((rate) => (
                  <div key={rate.currency}>
                    <strong>{rate.currency} 100</strong> = <strong>USD {fmtNum(100 / rate.rate_to_usd)}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 mt-6"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Exchange Rates'}
          </Button>
        </Card>
      </div>
    </Layout>
  );
}
